<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import { es } from 'date-fns/locale';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgendaTaskRow from '../../agenda/components/AgendaTaskRow.vue';
import LeadTasksAPI from 'dashboard/api/masifi/leadTasks';
import LeadTaskTypesAPI from 'dashboard/api/masifi/leadTaskTypes';
import { unwrap } from '../helper';
import LeadTaskDialog from './LeadTaskDialog.vue';

// Panel de Tareas de la ficha del lead (estilo Kommo): lista de pendientes con
// señal de urgencia por color, completadas con resultado. Las altas/ediciones/
// completar/borrar se delegan al LeadTaskDialog compartido (DRY); el panel solo
// mantiene la lista y refresca al terminar.
const props = defineProps({
  leadId: { type: [Number, String], required: true },
  assigneeOptions: { type: Array, default: () => [] },
});

const { t } = useI18n();

const tasks = ref([]);
const types = ref([]);
const isLoading = ref(false);

const dialog = ref(null);

const loadTasks = async () => {
  isLoading.value = true;
  try {
    const { data } = await LeadTasksAPI.get({ lead_id: props.leadId });
    tasks.value = unwrap(data);
  } catch (error) {
    useAlert(t('LEADS.TASK.SAVE_ERROR'));
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadTypes = async () => {
  try {
    const { data } = await LeadTaskTypesAPI.get();
    types.value = unwrap(data);
  } catch (error) {
    types.value = [];
  }
};

onMounted(() => {
  loadTasks();
  loadTypes();
});

const pendingTasks = computed(() =>
  tasks.value
    .filter(task => !task.completed_at)
    .toSorted((a, b) => new Date(a.due_at) - new Date(b.due_at))
);

const completedTasks = computed(() =>
  tasks.value
    .filter(task => task.completed_at)
    .toSorted((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
);

// Mapa id → name para evitar un .find() por render.
const typeNameMap = computed(() =>
  Object.fromEntries(types.value.map(ty => [ty.id, ty.name]))
);

const resolveTypeName = task => typeNameMap.value[task.lead_task_type_id] || '';

const formatDate = value => {
  if (!value) return '';
  try {
    return format(new Date(value), 'dd MMM HH:mm', { locale: es });
  } catch (error) {
    return '';
  }
};

// Los diálogos (alta/edición/completar/borrar) se delegan al LeadTaskDialog
// compartido (DRY); el panel solo conserva la lista y refresca al terminar.
const openNew = () => dialog.value?.openNew(props.leadId);
const openEdit = task => dialog.value?.openEdit(task);
const openComplete = task => dialog.value?.openComplete(task);
const remove = task => dialog.value?.remove(task);
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('LEADS.TASK.TITLE') }}
      </h3>
      <Button
        sm
        blue
        icon="i-lucide-plus"
        :label="t('LEADS.TASK.ADD')"
        @click="openNew"
      />
    </div>

    <!-- Vacío -->
    <div
      v-if="!pendingTasks.length && !completedTasks.length"
      class="flex flex-col items-center justify-center gap-2 py-8 text-center"
    >
      <span class="i-lucide-calendar-clock size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-10">{{ t('LEADS.TASK.EMPTY') }}</p>
    </div>

    <!-- Pendientes: reusar AgendaTaskRow (DRY) -->
    <div v-if="pendingTasks.length" class="flex flex-col gap-2">
      <span class="text-xs font-medium uppercase text-n-slate-10">
        {{ t('LEADS.TASK.PENDING_SECTION') }}
      </span>
      <ul class="flex flex-col gap-1.5">
        <AgendaTaskRow
          v-for="task in pendingTasks"
          :key="task.id"
          :task="task"
          :type-name="resolveTypeName(task)"
          :show-assignee="false"
          :show-lead-link="false"
          @edit="openEdit"
          @complete="openComplete"
          @remove="remove"
        />
      </ul>
    </div>

    <!-- Completadas -->
    <div v-if="completedTasks.length" class="flex flex-col gap-2">
      <span class="text-xs font-medium uppercase text-n-slate-10">
        {{ t('LEADS.TASK.COMPLETED_SECTION') }}
      </span>
      <ul class="flex flex-col gap-1.5">
        <li
          v-for="task in completedTasks"
          :key="task.id"
          class="flex flex-col gap-0.5 p-2.5 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-check-circle-2 size-3.5 text-n-teal-10" />
            <span class="text-sm line-through text-n-slate-10">
              {{ task.title }}
            </span>
            <span class="ml-auto text-xs text-n-slate-10">
              {{ formatDate(task.completed_at) }}
            </span>
          </div>
          <p
            v-if="task.result_text"
            class="pl-5 text-xs whitespace-pre-wrap text-n-slate-10"
          >
            {{ task.result_text }}
          </p>
        </li>
      </ul>
    </div>

    <!-- Diálogos compartidos (alta/edición/completar) -->
    <LeadTaskDialog
      ref="dialog"
      :assignee-options="assigneeOptions"
      @saved="loadTasks"
      @completed="loadTasks"
      @deleted="loadTasks"
    />
  </section>
</template>
